import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';

const normalizeImages = (images) =>
  (images || [])
    .map((img) => {
      if (!img) return null;
      if (typeof img === 'string') return { src: img, alt: '' };
      return {
        src: img.src || img.url || img.image_url || '',
        alt: img.alt || '',
      };
    })
    .filter((img) => img?.src);

const ImageLightbox = ({
  images = [],
  index = 0,
  open = false,
  onClose,
  onIndexChange,
  alt = 'Photo',
}) => {
  const slides = normalizeImages(images);
  const count = slides.length;
  const current = count ? Math.min(Math.max(index, 0), count - 1) : 0;
  const touchStartX = useRef(null);
  const thumbsRef = useRef(null);

  const goTo = useCallback(
    (nextIndex) => {
      if (!count) return;
      const wrapped = ((nextIndex % count) + count) % count;
      onIndexChange?.(wrapped);
    },
    [count, onIndexChange]
  );

  const go = useCallback(
    (delta) => {
      if (count < 2) return;
      goTo(current + delta);
    },
    [count, current, goTo]
  );

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'ArrowLeft') go(-1);
    };

    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, go]);

  useEffect(() => {
    if (!open || !thumbsRef.current) return;
    const active = thumbsRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [open, current]);

  if (!open || !count) return null;

  const slide = slides[current];
  const caption = slide.alt || alt;
  const counter = `${String(current + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`;

  const handleTouchStart = (event) => {
    touchStartX.current = event.changedTouches?.[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current == null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    go(delta < 0 ? 1 : -1);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col text-white animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={onClose}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Image
          src={slide.src}
          alt=""
          className="w-full h-full object-cover scale-110 blur-3xl opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
      </div>

      <header
        className="relative z-10 flex items-center justify-between gap-4 px-4 sm:px-6 py-3 sm:py-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.22em] text-white/50 font-medium">
            Gallery
          </p>
          <p className="mt-0.5 text-sm sm:text-base font-medium text-white/95 truncate max-w-[52vw] sm:max-w-md">
            {caption}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <span className="hidden sm:inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium tabular-nums tracking-wide backdrop-blur-md">
            {counter}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-white/15 bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-colors"
            aria-label="Close photos"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
      </header>

      <div
        className="relative z-10 flex-1 min-h-0 flex items-center justify-center px-3 sm:px-20 py-2"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          key={slide.src}
          className="w-full h-full flex items-center justify-center animate-in fade-in zoom-in-95 duration-300"
        >
          <Image
            src={slide.src}
            alt={caption}
            className="max-w-full max-h-full w-auto h-auto object-contain select-none rounded-md shadow-[0_24px_80px_rgba(0,0,0,0.45)]"
            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
          />
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/15 bg-white/10 hover:bg-white/20 text-white backdrop-blur-md flex items-center justify-center transition-all hover:scale-105"
              aria-label="Previous photo"
            >
              <Icon name="ChevronLeft" size={22} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/15 bg-white/10 hover:bg-white/20 text-white backdrop-blur-md flex items-center justify-center transition-all hover:scale-105"
              aria-label="Next photo"
            >
              <Icon name="ChevronRight" size={22} />
            </button>
          </>
        )}
      </div>

      <footer
        className="relative z-10 flex-shrink-0 px-4 sm:px-6 pb-4 sm:pb-5 pt-2"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sm:hidden mb-3 flex justify-center">
          <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium tabular-nums tracking-wide backdrop-blur-md">
            {counter}
          </span>
        </div>

        {count > 1 && (
          <div
            ref={thumbsRef}
            className="flex gap-2.5 overflow-x-auto pb-1 justify-start sm:justify-center scrollbar-thin"
          >
            {slides.map((item, i) => (
              <button
                key={`${item.src}-${i}`}
                type="button"
                data-active={i === current ? 'true' : 'false'}
                onClick={() => goTo(i)}
                className={`relative flex-shrink-0 w-[72px] h-14 rounded-lg overflow-hidden transition-all duration-200 ${
                  i === current
                    ? 'ring-2 ring-white ring-offset-2 ring-offset-black/40 scale-105 opacity-100'
                    : 'opacity-45 hover:opacity-90'
                }`}
                aria-label={`Photo ${i + 1}`}
              >
                <Image src={item.src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <p className="hidden sm:block mt-3 text-center text-[11px] tracking-wide text-white/35">
          {count > 1 ? 'Use arrows or swipe · Esc to close' : 'Esc to close'}
        </p>
      </footer>
    </div>,
    document.body
  );
};

export default ImageLightbox;
